import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { FileText, Pencil, Printer, ArrowRightLeft } from "lucide-react";
import { useAuth } from "@/AuthContext";
import { API } from "@/config";

export const EstimateView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { token } = useAuth();
  const [estimate, setEstimate] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => { fetchEstimate(); fetchLogo(); }, [id]);

  const fetchEstimate = async () => { try { setEstimate((await axios.get(`${API}/estimates/${id}`)).data); } catch { toast.error("Error fetching estimate"); } };
  const fetchLogo = async () => { try { setLogoUrl(URL.createObjectURL((await axios.get(`${API}/logo`, { responseType: 'blob' })).data)); } catch { } };
  const handlePrint = () => window.print();

  const handleUpdateStatus = async (s) => {
    try { await axios.put(`${API}/estimates/${id}/status?status=${s}`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success(`Estimate marked as ${s}`); fetchEstimate(); }
    catch { toast.error("Error updating status"); }
  };

  const handleConvertToInvoice = async () => {
    if (!window.confirm("Convert this estimate to an invoice? This will create a new invoice with the same items, payment plan, and details.")) return;
    try { const r = await axios.post(`${API}/estimates/${id}/convert-to-invoice`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success(`Converted to ${r.data.invoice_number}`); navigate(`/invoices/${r.data.invoice_id}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Error converting"); }
  };

  const handleSendEmail = () => {
    if (!estimate) return;
    let paymentInfo = '';
    if (estimate.payment_type === 'financing' && estimate.payment_plan?.enabled) {
      paymentInfo = `\nPayment Plan: Financing\nDown Payment: $${estimate.payment_plan.down_payment.toFixed(2)}\nMonthly Payment: $${estimate.payment_plan.monthly_payment.toFixed(2)} x ${estimate.payment_plan.number_of_months} months\nAPR: ${estimate.payment_plan.apr}%`;
    } else if (estimate.payment_type === 'lease_to_own' && estimate.lease_plan?.enabled) {
      paymentInfo = `\nPayment Plan: Lease-to-Own\nMonthly Payment: $${estimate.lease_plan.monthly_payment.toFixed(2)}\nTotal Lease Term: ${estimate.lease_plan.total_months} months\nDown Payment Period: ${estimate.lease_plan.down_payment_months} months\nAPR: ${estimate.lease_plan.apr}%`;
    }
    const subject = encodeURIComponent(`Estimate ${estimate.estimate_number} from Ellantes LLC`);
    const body = encodeURIComponent(`Dear ${estimate.customer_name},\n\nThank you for your interest. Please find below the estimate details:\n\nEstimate Number: ${estimate.estimate_number}\nDate: ${new Date(estimate.created_at).toLocaleDateString()}\nValid Until: ${estimate.valid_until ? new Date(estimate.valid_until).toLocaleDateString() : 'N/A'}\n\nItems:\n${estimate.items.map(i => `- ${i.product_name} (Qty: ${i.quantity}) - $${i.total.toFixed(2)}`).join('\n')}\n\nTotal: $${estimate.total.toFixed(2)}${paymentInfo}${estimate.notes ? `\n\nNotes: ${estimate.notes}` : ''}\n\nPlease let us know if you have any questions.\n\nBest regards,\n${estimate.sales_agent || 'Ellantes LLC'}\nEllantes LLC\n1309 Coffeen Avenue, Sheridan, WY 82801`);
    window.open(`https://mail.google.com/mail/?view=cm&fs=1&to=${estimate.customer_email}&su=${subject}&body=${body}`, '_blank');
  };

  if (!estimate) return <div>Loading...</div>;

  const pp = estimate.payment_plan;
  const lp = estimate.lease_plan;
  const isFinancing = estimate.payment_type === 'financing' && pp?.enabled;
  const isLease = estimate.payment_type === 'lease_to_own' && lp?.enabled;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between print:hidden">
        <h1 className="text-4xl font-bold" data-testid="estimate-view-title">Estimate Details</h1>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => navigate('/estimates')} data-testid="back-to-estimates-btn">Back</Button>
          {estimate.status !== 'converted' && <Button variant="outline" onClick={() => navigate(`/estimates/${estimate.id}/edit`)} data-testid="edit-estimate-btn"><Pencil className="h-4 w-4 mr-2" />Edit</Button>}
          <Button onClick={handlePrint} data-testid="print-estimate-btn"><Printer className="h-4 w-4 mr-2" />Print Estimate</Button>
          <Button variant="outline" onClick={handleSendEmail} data-testid="email-estimate-btn"><FileText className="h-4 w-4 mr-2" />Email to Customer</Button>
          {estimate.status !== 'converted' && (
            <>
              {estimate.status === 'draft' && <Button variant="outline" onClick={() => handleUpdateStatus('sent')} data-testid="mark-sent-btn">Mark as Sent</Button>}
              {(estimate.status === 'sent' || estimate.status === 'draft') && <Button variant="outline" onClick={() => handleUpdateStatus('accepted')} data-testid="mark-accepted-btn" className="text-green-700 border-green-300 hover:bg-green-50">Mark as Accepted</Button>}
              <Button onClick={handleConvertToInvoice} data-testid="convert-to-invoice-btn" className="bg-purple-600 hover:bg-purple-700"><ArrowRightLeft className="h-4 w-4 mr-2" />Convert to Invoice</Button>
            </>
          )}
        </div>
      </div>

      {estimate.status === 'converted' && estimate.converted_invoice_id && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-center justify-between print:hidden" data-testid="converted-notice">
          <span className="text-purple-800 font-medium">This estimate has been converted to an invoice.</span>
          <Button variant="outline" size="sm" onClick={() => navigate(`/invoices/${estimate.converted_invoice_id}`)} data-testid="go-to-invoice-btn">View Invoice</Button>
        </div>
      )}

      <Card className="print:shadow-none" data-testid="estimate-card">
        <CardContent className="p-8">
          <div className="space-y-8 invoice-content">
            {/* Company Header */}
            <div className="flex justify-between items-start border-b-2 pb-6">
              <div>
                {logoUrl && <img src={logoUrl} alt="Ellantes Logo" className="h-20 mb-4" data-testid="estimate-company-logo" />}
                <div className="space-y-1">
                  <div className="text-xl font-bold">Ellantes LLC</div>
                  <div className="text-sm text-gray-600">1309 Coffeen Avenue</div>
                  <div className="text-sm text-gray-600">Sheridan, WY 82801</div>
                  <div className="text-sm text-gray-600">United States</div>
                  <div className="text-sm text-gray-600 mt-2">EIN: 33-3145594</div>
                </div>
              </div>
              <div className="text-right">
                <h2 className="text-3xl font-bold mb-4">ESTIMATE</h2>
                <div className="text-2xl font-bold text-blue-600" data-testid="estimate-number">{estimate.estimate_number}</div>
                <div className="text-sm text-gray-600 mt-2">Date: {new Date(estimate.created_at).toLocaleDateString()}</div>
                {estimate.valid_until && <div className="text-sm text-gray-600">Valid Until: {new Date(estimate.valid_until).toLocaleDateString()}</div>}
              </div>
            </div>

            {/* Customer & Sales Agent */}
            <div className="grid grid-cols-2 gap-8">
              <div>
                <h3 className="font-bold text-lg mb-2">Prepared For:</h3>
                <div data-testid="estimate-customer-info">
                  <div className="font-semibold">{estimate.customer_name}</div>
                  <div>{estimate.customer_address}</div>
                  <div>{estimate.customer_email}</div>
                  <div>{estimate.customer_phone}</div>
                </div>
              </div>
              <div className="space-y-4">
                {estimate.sales_agent && <div><h3 className="font-bold text-lg mb-2">Sales Agent:</h3><div className="font-semibold text-blue-600" data-testid="estimate-sales-agent-name">{estimate.sales_agent}</div></div>}
                <div className="print:hidden">
                  <h3 className="font-bold text-lg mb-2">Status:</h3>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${estimate.status === 'sent' ? 'bg-blue-100 text-blue-800' : estimate.status === 'accepted' ? 'bg-green-100 text-green-800' : estimate.status === 'expired' ? 'bg-red-100 text-red-800' : estimate.status === 'converted' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`} data-testid="estimate-view-status">{estimate.status.charAt(0).toUpperCase() + estimate.status.slice(1)}</span>
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div>
              <Table>
                <TableHeader><TableRow><TableHead>Description</TableHead><TableHead>Quantity</TableHead><TableHead>Price</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                <TableBody>
                  {estimate.items.map((item, i) => (
                    <TableRow key={i}><TableCell><div className="font-medium">{item.product_name}</div><div className="text-sm text-gray-600">{item.description}</div></TableCell><TableCell>{item.quantity}</TableCell><TableCell>${item.price.toFixed(2)}</TableCell><TableCell className="text-right">${item.total.toFixed(2)}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between"><span>Subtotal:</span><span data-testid="estimate-view-subtotal">${estimate.subtotal.toFixed(2)}</span></div>
                {estimate.discount_amount > 0 && <div className="flex justify-between text-green-600"><span>Discount {estimate.discount_type === "percentage" ? `(${estimate.discount_value}%)` : ""}:</span><span data-testid="estimate-view-discount">-${estimate.discount_amount.toFixed(2)}</span></div>}
                {estimate.tax_rate > 0 && <div className="flex justify-between"><span>Tax ({estimate.tax_rate}%):</span><span data-testid="estimate-view-tax">${estimate.tax_amount.toFixed(2)}</span></div>}
                <div className="flex justify-between text-xl font-bold border-t pt-2"><span>Total:</span><span data-testid="estimate-view-total">${estimate.total.toFixed(2)}</span></div>
              </div>
            </div>

            {/* Payment Plan Section */}
            {isFinancing && (
              <div className="border-2 border-blue-200 rounded-lg p-6 bg-blue-50 print-keep-together" data-testid="estimate-financing-section">
                <h3 className="font-bold text-lg mb-4 text-blue-800">FINANCING TERMS</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-600">Down Payment:</span><span className="font-bold ml-2">${pp.down_payment.toFixed(2)}</span></div>
                  <div><span className="text-gray-600">Amount Financed:</span><span className="font-bold ml-2">${pp.total_financed.toFixed(2)}</span></div>
                  <div><span className="text-gray-600">APR:</span><span className="font-bold ml-2">{pp.apr}%</span></div>
                  <div><span className="text-gray-600">Number of Months:</span><span className="font-bold ml-2">{pp.number_of_months}</span></div>
                  <div className="col-span-2 pt-3 border-t border-blue-200">
                    <span className="text-gray-600 text-base">Monthly Payment:</span>
                    <span className="font-bold text-2xl ml-2 text-blue-700" data-testid="estimate-view-monthly-payment">${pp.monthly_payment.toFixed(2)}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-600">Total Amount to be Paid:</span>
                    <span className="font-bold ml-2">${(pp.down_payment + (pp.monthly_payment * pp.number_of_months)).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Lease-to-Own Section */}
            {isLease && (
              <div className="border-2 border-green-200 rounded-lg p-6 bg-green-50 print-keep-together" data-testid="estimate-lease-section">
                <h3 className="font-bold text-lg mb-4 text-green-800">LEASE-TO-OWN TERMS</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-600">Total Lease Term:</span><span className="font-bold ml-2">{lp.total_months} months</span></div>
                  <div><span className="text-gray-600">APR:</span><span className="font-bold ml-2">{lp.apr}%</span></div>
                  <div><span className="text-gray-600">Down Payment Period:</span><span className="font-bold ml-2">{lp.down_payment_months} months</span></div>
                  <div><span className="text-gray-600">Down Payment Amount:</span><span className="font-bold ml-2">${(lp.monthly_payment * lp.down_payment_months).toFixed(2)}</span></div>
                  <div className="col-span-2 pt-3 border-t border-green-200">
                    <span className="text-gray-600 text-base">Monthly Payment:</span>
                    <span className="font-bold text-2xl ml-2 text-green-700" data-testid="estimate-view-lease-monthly">${lp.monthly_payment.toFixed(2)}</span>
                  </div>
                  <div><span className="text-gray-600">Remaining Monthly Payments:</span><span className="font-bold ml-2">{lp.total_months - lp.down_payment_months}</span></div>
                  <div><span className="text-gray-600">Total Amount to be Paid:</span><span className="font-bold ml-2">${(lp.monthly_payment * lp.total_months).toFixed(2)}</span></div>
                </div>
                <p className="text-xs text-gray-600 mt-4 italic">Ownership transfers upon full payment. During the lease term, Ellantes LLC retains ownership of all equipment.</p>
              </div>
            )}

            {/* CC Authorization - Print Only */}
            {(isFinancing || isLease) && estimate.cc_authorization && estimate.cc_authorization.cardholder_name && (
              <div className="border-2 border-gray-300 rounded-lg p-6 print-keep-together" data-testid="estimate-cc-section">
                <h3 className="font-bold text-lg mb-4">CREDIT CARD AUTHORIZATION</h3>
                <p className="text-sm text-gray-600 mb-4">I authorize Ellantes LLC to charge my credit card for the monthly payment amount as outlined above.</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-600">Cardholder Name:</span><span className="font-bold ml-2">{estimate.cc_authorization.cardholder_name}</span></div>
                  <div><span className="text-gray-600">Card Number:</span><span className="font-bold ml-2">{estimate.cc_authorization.card_number}</span></div>
                  <div><span className="text-gray-600">Expiration:</span><span className="font-bold ml-2">{estimate.cc_authorization.expiration_date}</span></div>
                  <div><span className="text-gray-600">CVV:</span><span className="font-bold ml-2">{estimate.cc_authorization.cvv}</span></div>
                  <div><span className="text-gray-600">Billing ZIP:</span><span className="font-bold ml-2">{estimate.cc_authorization.billing_zip}</span></div>
                  <div><span className="text-gray-600">ID Number:</span><span className="font-bold ml-2">{estimate.cc_authorization.id_number}</span></div>
                </div>
                <div className="mt-6 grid grid-cols-2 gap-8">
                  <div><div className="border-b-2 border-gray-800 mb-2 h-12"></div><div className="text-sm font-semibold">Cardholder Signature</div></div>
                  <div><div className="border-b-2 border-gray-800 mb-2 h-12"></div><div className="text-sm font-semibold">Date</div></div>
                </div>
              </div>
            )}

            {/* Notes */}
            {estimate.notes && <div><h3 className="font-bold mb-2">Notes:</h3><p className="text-sm bg-gray-50 p-4 rounded-lg" data-testid="estimate-view-notes">{estimate.notes}</p></div>}

            {/* Disclaimer */}
            <div className="border-t pt-6 mt-6">
              <p className="text-sm text-gray-500 italic">This is an estimate only and not a binding agreement. Prices and availability are subject to change.{estimate.valid_until && ` This estimate is valid until ${new Date(estimate.valid_until).toLocaleDateString()}.`}</p>
            </div>

            {/* Contact */}
            <div className="hidden print:block mt-8 p-6 bg-gray-50 border rounded-lg print-keep-together">
              <h3 className="font-bold text-lg mb-4">CONTACT INFORMATION</h3>
              <div className="grid grid-cols-2 gap-6">
                <div><h4 className="font-semibold mb-2">Email</h4><p className="text-sm">Ellantesmarketing@gmail.com</p></div>
                <div><h4 className="font-semibold mb-2">Address</h4><p className="text-sm">1309 Coffeen Avenue</p><p className="text-sm">Sheridan, WY 82801</p></div>
              </div>
            </div>

            {/* Acceptance */}
            <div className="hidden print:block mt-12 pt-8 border-t-2 border-gray-800 signature-section">
              <h3 className="font-bold text-lg mb-6">ACCEPTANCE OF ESTIMATE</h3>
              <p className="mb-8 text-sm leading-relaxed">By signing below, the customer acknowledges review of this estimate{(isFinancing || isLease) ? ', including the payment terms,' : ''} and wishes to proceed with the purchase as described above.</p>
              <div className="grid grid-cols-2 gap-8 mt-8">
                <div><div className="border-b-2 border-gray-800 mb-2 h-16"></div><div className="text-sm font-semibold">Customer Signature</div></div>
                <div><div className="border-b-2 border-gray-800 mb-2 h-16"></div><div className="text-sm font-semibold">Date</div></div>
              </div>
              <div className="mt-8"><div className="border-b-2 border-gray-800 mb-2 h-12 w-2/3"></div><div className="text-sm font-semibold">Print Name</div></div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
